/**
 * Public Links Management Component
 * 
 * Complete standalone management interface for public campus summary links.
 * Includes:
 * - View all public links across teams
 * - Search for schools to create new links
 * - Upload logos
 * - Configure URL slugs
 * - Enable/disable links
 * - Delete links
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Drawer,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn,
  ToggleOff,
  School as SchoolIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  CloudUpload as UploadIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import apiClient from '../../../utils/apiClient';
import { formatSchoolName } from '../../../utils/schoolUtils';

const PublicLinksManagement = () => {
  // State for list view
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  
  // State for create/edit drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // false = create, true = edit
  
  // State for school selection
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [teamSchools, setTeamSchools] = useState([]);
  const [loadingTeamSchools, setLoadingTeamSchools] = useState(false);
  
  // State for team selection (when creating)
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loadingTeams, setLoadingTeams] = useState(false);
  
  // State for configuration
  const [config, setConfig] = useState({
    enable_public_summary: true,
    public_summary_school_uuid: '',
    custom_slug: '',
    logo_url: null,
    funded_by_csgf: false
  });
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [slugError, setSlugError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);

  // Fetch all public links
  const fetchPublicLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/v2/admin/public-links');
      setLinks(response.links || []);
    } catch (err) {
      console.error('Error fetching public links:', err);
      setError('Failed to fetch public links. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all teams
  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await apiClient.get('/v2/admin/teams?getAllTeams=true');
      setTeams(response.teams || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setConfigError('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  // Fetch schools for a team
  const fetchTeamSchools = async (teamId) => {
    try {
      setLoadingTeamSchools(true);
      const response = await apiClient.get(`/v2/admin/teams/${teamId}/schools`);
      setTeamSchools(response.schools || []);
    } catch (err) {
      console.error('Error fetching team schools:', err);
      setConfigError('Failed to load team schools');
      setTeamSchools([]);
    } finally {
      setLoadingTeamSchools(false);
    }
  };

  useEffect(() => {
    fetchPublicLinks();
  }, []);

  // Filter links based on search and status
  const filteredLinks = useMemo(() => {
    let filtered = links;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(link => 
        statusFilter === 'active' ? link.enable_public_summary : !link.enable_public_summary
      );
    }

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(link =>
        link.school_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
        link.team_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
        link.custom_slug?.toLowerCase().includes(lowerCaseSearchTerm) ||
        link.city?.toLowerCase().includes(lowerCaseSearchTerm) ||
        link.state?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    return filtered;
  }, [links, searchTerm, statusFilter]);

  // Summary stats
  const stats = useMemo(() => {
    return {
      total: links.length,
      active: links.filter(l => l.enable_public_summary).length,
      inactive: links.filter(l => !l.enable_public_summary).length
    };
  }, [links]);

  // Handle create new link
  const handleCreateNew = () => {
    setEditMode(false);
    setSelectedSchool(null);
    setSelectedTeam(null);
    setTeamSchools([]);
    setConfig({
      enable_public_summary: true,
      public_summary_school_uuid: '',
      custom_slug: '',
      logo_url: null,
      funded_by_csgf: false
    });
    setConfigError(null);
    setSuccess(null);
    setSlugError(null);
    fetchTeams();
    setDrawerOpen(true);
  };

  // Handle edit link
  const handleEdit = async (link) => {
    setEditMode(true);
    const team = { id: link.team_id, name: link.team_name };
    setSelectedTeam(team);
    
    // Fetch team schools first
    try {
      setLoadingTeamSchools(true);
      const response = await apiClient.get(`/v2/admin/teams/${link.team_id}/schools`);
      const schools = response.schools || [];
      setTeamSchools(schools);
      
      // Find and set the selected school
      const school = schools.find(s => s.uuid === link.school_uuid);
      setSelectedSchool(school);
    } catch (err) {
      console.error('Error fetching team schools:', err);
      setConfigError('Failed to load team schools');
    } finally {
      setLoadingTeamSchools(false);
    }
    
    setConfig({
      enable_public_summary: link.enable_public_summary,
      public_summary_school_uuid: link.school_uuid,
      custom_slug: link.custom_slug || '',
      logo_url: link.logo_url || null,
      funded_by_csgf: link.funded_by_csgf || false
    });
    setConfigError(null);
    setSuccess(null);
    setSlugError(null);
    setDrawerOpen(true);
  };

  // Handle toggle status
  const handleToggleStatus = async (link) => {
    try {
      await apiClient.put(`/v2/admin/teams/${link.team_id}/public-summary`, {
        enable_public_summary: !link.enable_public_summary,
        public_summary_school_uuid: link.school_uuid,
        custom_slug: link.custom_slug
      });
      fetchPublicLinks();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle delete link
  const handleDeleteClick = (link) => {
    setLinkToDelete(link);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;
    
    try {
      await apiClient.delete(`/v2/admin/teams/${linkToDelete.team_id}/public-summary`);
      fetchPublicLinks();
      setDeleteDialogOpen(false);
      setLinkToDelete(null);
    } catch (err) {
      console.error('Error deleting link:', err);
      setError('Failed to delete link. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle copy URL
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(`${window.location.origin}${url}`);
    setSuccess('URL copied to clipboard!');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Generate slug
  const generateSlug = () => {
    if (!selectedSchool) {
      setConfigError('Please select a school first');
      return;
    }
    
    const teamName = selectedTeam?.name || '';
    const schoolName = selectedSchool?.name || '';
    const combined = `${teamName} ${schoolName}`.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    setConfig(prev => ({ ...prev, custom_slug: combined }));
    setSlugError(null);
  };

  // Validate slug
  const validateSlug = (slug) => {
    if (!slug) {
      setSlugError(null);
      return false;
    }
    
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(slug)) {
      setSlugError('Slug can only contain lowercase letters, numbers, and hyphens');
      return false;
    }
    
    setSlugError(null);
    return true;
  };

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedTeam?.id) {
      setConfigError('Please select a team first');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setConfigError('Please upload a PNG, JPG, SVG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setConfigError('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setConfigError(null);

      const formData = new FormData();
      formData.append('logo', file);

      const response = await apiClient.post(
        `/v2/admin/teams/${selectedTeam.id}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setConfig(prev => ({ ...prev, logo_url: response.logo_url }));
      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      setConfigError(err.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // Handle logo delete
  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this logo?')) {
      return;
    }

    if (!selectedTeam?.id) {
      setConfigError('No team selected');
      return;
    }

    try {
      setUploading(true);
      setConfigError(null);

      await apiClient.delete(`/v2/admin/teams/${selectedTeam.id}/logo`);
      
      setConfig(prev => ({ ...prev, logo_url: null }));
      setSuccess('Logo deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error deleting logo:', err);
      setConfigError(err.message || 'Failed to delete logo');
    } finally {
      setUploading(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setConfigError(null);
      setSuccess(null);

      // Validation
      if (!selectedTeam?.id) {
        setConfigError('Please select a team');
        return;
      }

      if (config.enable_public_summary) {
        if (!config.public_summary_school_uuid) {
          setConfigError('Please select a school');
          return;
        }
        if (!config.custom_slug) {
          setConfigError('Please set a URL slug');
          return;
        }
        if (!validateSlug(config.custom_slug)) {
          return;
        }
      }

      await apiClient.put(`/v2/admin/teams/${selectedTeam.id}/public-summary`, {
        enable_public_summary: config.enable_public_summary,
        public_summary_school_uuid: config.public_summary_school_uuid || null,
        custom_slug: config.custom_slug || null,
        funded_by_csgf: config.funded_by_csgf
      });

      setSuccess(editMode ? 'Link updated successfully!' : 'Link created successfully!');
      setTimeout(() => {
        setSuccess(null);
        setDrawerOpen(false);
        fetchPublicLinks();
      }, 1500);
      
    } catch (err) {
      console.error('Error saving config:', err);
      setConfigError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle school selection
  const handleSchoolSelect = (event) => {
    const schoolUuid = event.target.value;
    const school = teamSchools.find(s => s.uuid === schoolUuid);
    setSelectedSchool(school);
    setConfig(prev => ({ 
      ...prev, 
      public_summary_school_uuid: schoolUuid 
    }));
  };

  // Handle team selection
  const handleTeamSelect = (event) => {
    const teamId = event.target.value;
    const team = teams.find(t => t.id === teamId);
    setSelectedTeam(team);
    
    // Clear school selection and fetch schools for this team
    setSelectedSchool(null);
    setConfig(prev => ({ ...prev, public_summary_school_uuid: '' }));
    
    if (teamId) {
      fetchTeamSchools(teamId);
    } else {
      setTeamSchools([]);
    }
  };

  const publicUrl = config.custom_slug 
    ? `${window.location.origin}/campuses/${config.custom_slug}`
    : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublicIcon fontSize="large" color="primary" />
          Public Campus Links
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage public-facing campus summary pages. Create links, upload logos, and control visibility.
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="caption">Total Links</Typography>
            <Typography variant="h4">{stats.total}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="caption">Active</Typography>
            <Typography variant="h4" color="success.main">{stats.active}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="caption">Inactive</Typography>
            <Typography variant="h4" color="text.secondary">{stats.inactive}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchPublicLinks} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleCreateNew}
          sx={{ ml: 'auto' }}
        >
          Create New Link
        </Button>
      </Paper>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredLinks.length === 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          {searchTerm || statusFilter !== 'all' 
            ? 'No links match your search criteria.' 
            : 'No public campus links found. Click "Create New Link" to get started.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>School Name</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Public URL</TableCell>
                <TableCell>Logo</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLinks.map((link) => (
                <TableRow key={link.team_id} hover>
                  <TableCell>
                    <Chip
                      label={link.enable_public_summary ? 'Active' : 'Inactive'}
                      color={link.enable_public_summary ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatSchoolName(link.school_name)}</TableCell>
                  <TableCell>{link.team_name}</TableCell>
                  <TableCell>
                    {link.city && link.state ? `${link.city}, ${link.state}` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {link.public_url ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {link.custom_slug}
                        </Typography>
                        <Tooltip title="Copy URL">
                          <IconButton size="small" onClick={() => handleCopyUrl(link.public_url)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Page">
                          <IconButton 
                            size="small" 
                            onClick={() => window.open(link.public_url, '_blank')}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {link.logo_url ? (
                      <Box
                        component="img"
                        src={link.logo_url}
                        alt="Logo"
                        sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, border: '1px solid #eee' }}
                      />
                    ) : (
                      <SchoolIcon sx={{ color: 'text.disabled', fontSize: 30 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={link.enable_public_summary ? 'Deactivate' : 'Activate'}>
                      <IconButton 
                        onClick={() => handleToggleStatus(link)} 
                        color={link.enable_public_summary ? 'warning' : 'success'}
                        size="small"
                      >
                        {link.enable_public_summary ? <ToggleOn /> : <ToggleOff />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEdit(link)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteClick(link)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600 } }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              {editMode ? 'Edit Public Link' : 'Create New Public Link'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Team Selection (only for create mode) */}
          {!editMode && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Team</InputLabel>
              <Select
                value={selectedTeam?.id || ''}
                label="Select Team"
                onChange={handleTeamSelect}
                disabled={loadingTeams}
              >
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* School Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select School</InputLabel>
            <Select
              value={config.public_summary_school_uuid || ''}
              label="Select School"
              onChange={handleSchoolSelect}
              disabled={editMode || !selectedTeam || loadingTeamSchools}
            >
              {loadingTeamSchools ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading schools...
                </MenuItem>
              ) : teamSchools.length === 0 ? (
                <MenuItem disabled>
                  {selectedTeam ? 'No schools assigned to this team' : 'Select a team first'}
                </MenuItem>
              ) : (
                teamSchools.map((school) => (
                  <MenuItem key={school.uuid} value={school.uuid}>
                    {school.name} - {school.city}, {school.state}
                  </MenuItem>
                ))
              )}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              {editMode ? "School cannot be changed after creation" : "Select from schools assigned to this team"}
            </Typography>
          </FormControl>

          {/* Enable Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={config.enable_public_summary}
                onChange={(e) => setConfig(prev => ({ ...prev, enable_public_summary: e.target.checked }))}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Enable Public Summary</Typography>
                <Typography variant="caption" color="text.secondary">
                  Make this campus summary accessible to the public
                </Typography>
              </Box>
            }
            sx={{ mb: 3 }}
          />

          {/* CSGF Funding Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={config.funded_by_csgf}
                onChange={(e) => setConfig(prev => ({ ...prev, funded_by_csgf: e.target.checked }))}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Funded by CSGF?</Typography>
                <Typography variant="caption" color="text.secondary">
                  Show Charter School Growth Fund attribution on public page
                </Typography>
              </Box>
            }
            sx={{ mb: 3 }}
          />

          {config.enable_public_summary && (
            <>
              {/* URL Slug */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="URL Slug"
                  value={config.custom_slug}
                  onChange={(e) => {
                    const slug = e.target.value.toLowerCase();
                    setConfig(prev => ({ ...prev, custom_slug: slug }));
                    validateSlug(slug);
                  }}
                  helperText={
                    slugError || 
                    "Lowercase letters, numbers, and hyphens only. This will be used in the public URL."
                  }
                  error={!!slugError}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={generateSlug} disabled={!selectedSchool}>
                        Generate
                      </Button>
                    )
                  }}
                />
                {config.custom_slug && !slugError && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <strong>Public URL:</strong> {publicUrl}
                  </Alert>
                )}
              </Box>

              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  School Logo
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Upload a logo to display on the public summary page (PNG, JPG, SVG, or WebP, max 5MB)
                </Typography>

                {config.logo_url ? (
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      component="img"
                      src={config.logo_url}
                      alt="Team logo"
                      sx={{ width: 80, height: 80, objectFit: 'contain' }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Logo uploaded
                      </Typography>
                    </Box>
                    <IconButton 
                      color="error" 
                      onClick={handleLogoDelete}
                      disabled={uploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                    disabled={uploading || !selectedTeam}
                    fullWidth
                  >
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                    />
                  </Button>
                )}
              </Box>

              {/* Preview Link */}
              {publicUrl && (
                <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Public URL Ready</Typography>
                  <Link href={publicUrl} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {publicUrl}
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                </Alert>
              )}
            </>
          )}

          {/* Messages */}
          {configError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setConfigError(null)}>
              {configError}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button onClick={() => setDrawerOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !!slugError || !selectedTeam || !selectedSchool}
              startIcon={saving && <CircularProgress size={20} />}
            >
              {saving ? 'Saving...' : (editMode ? 'Update Link' : 'Create Link')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Public Link</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the public link for <strong>{linkToDelete?.school_name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicLinksManagement;
